import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { entityKeys } from './entity.query';

/**
 * Mutation hooks for the admin listing (entity) moderation surface —
 * write endpoints under `POST/PUT/PATCH/DELETE /api/admin/entities/*`.
 *
 * IMPORTANT: every endpoint here sends a JSON body, so its path MUST be present
 * in the proxy's `JSON_BODY_PAYLOAD_ENDPOINTS` allowlist (src/server/lib/utils.ts).
 * Without that, the proxy flattens the body to FormData and the backend's
 * `request.get_json()` returns None — the action silently no-ops.
 */

const BASE = '/api/admin/entities';

/** Canonical listing statuses accepted by the transition endpoint. */
export type EntityStatus =
  | 'LIVE'
  | 'REVIEW'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'INACTIVE'
  | 'DRAFT'
  | 'ARCHIVED'
  | 'DELETED';

/** Whitelisted boolean/int flags the flag endpoint accepts. */
export type EntityFlagField =
  | 'active'
  | 'published'
  | 'completed'
  | 'review'
  | 'rejected'
  | 'archive'
  | 'archive_perm'
  | 'deleted'
  | 'featured'
  | 'tester'
  | 'verified'
  | 'is_3d'
  | 'is_360'
  | 'is_virtual_tour';

// Invalidate everything scoped to entities plus the listing table/stats after a
// write, so the UI reflects moderation changes immediately.
const useEntityInvalidator = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: entityKeys.all });
    qc.invalidateQueries({ queryKey: ['listings'] });
    qc.invalidateQueries({ queryKey: ['listing-stats'] });
  };
};

// ─── Single-entity moderation ────────────────────────────────────────────────

/** Toggle a single whitelisted flag. `PATCH /:eid/flag` — `{ field, value }`. */
export const useSetEntityFlagMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; field: EntityFlagField; value: 0 | 1 }) =>
        request<any>(`${BASE}/${vars.eid}/flag`, 'PATCH', {
          field: vars.field,
          value: vars.value,
        }),
      'Failed to update the listing flag'
    ),
    onSuccess: invalidate,
  });
};

/**
 * Canonical status transition (approve/reject/suspend/pause/publish).
 * `POST /:eid/transition` — `{ status, reason?, version? }`.
 */
export const useTransitionEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; status: EntityStatus; reason?: string; version?: number }) =>
        request<any>(`${BASE}/${vars.eid}/transition`, 'POST', {
          status: vars.status,
          ...(vars.reason ? { reason: vars.reason } : {}),
          ...(vars.version != null ? { version: vars.version } : {}),
        }),
      'Failed to change the listing status'
    ),
    onSuccess: invalidate,
  });
};

/** Soft-delete a listing. `DELETE /:eid?version=`. */
export const useDeleteEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; version?: number }) =>
        request<any>(
          `${BASE}/${vars.eid}${vars.version != null ? `?version=${vars.version}` : ''}`,
          'DELETE'
        ),
      'Failed to delete the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Restore a soft-deleted listing. `POST /:eid/undelete`. */
export const useUndeleteEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; version?: number }) =>
        request<any>(`${BASE}/${vars.eid}/undelete`, 'POST', {
          ...(vars.version != null ? { version: vars.version } : {}),
        }),
      'Failed to restore the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Update listing fields. `PUT /:eid`. */
export const useUpdateEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; fields: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.eid}`, 'PUT', vars.fields),
      'Failed to update the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Add/remove tags. `PUT /:eid/tags` — `{ add: [tid], remove: [tid] }`. */
export const useUpdateEntityTagsMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; add?: number[]; remove?: number[] }) =>
        request<any>(`${BASE}/${vars.eid}/tags`, 'PUT', {
          add: vars.add ?? [],
          remove: vars.remove ?? [],
        }),
      'Failed to update tags'
    ),
    onSuccess: invalidate,
  });
};

/** Update attributes/specs. `PUT /:eid/attributes` — `{ attributes: [...] }`. */
export const useUpdateEntityAttributesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; attributes: Array<Record<string, unknown>> }) =>
        request<any>(`${BASE}/${vars.eid}/attributes`, 'PUT', {
          attributes: vars.attributes,
        }),
      'Failed to update attributes'
    ),
    onSuccess: invalidate,
  });
};

// ─── Price CRUD ──────────────────────────────────────────────────────────────

export const useCreateEntityPriceMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; price: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.eid}/price`, 'POST', vars.price),
      'Failed to create the price'
    ),
    onSuccess: invalidate,
  });
};

export const useUpdateEntityPriceMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; pricid: number; price: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.eid}/price/${vars.pricid}`, 'PUT', vars.price),
      'Failed to update the price'
    ),
    onSuccess: invalidate,
  });
};

export const useDeleteEntityPriceMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; pricid: number }) =>
        request<any>(`${BASE}/${vars.eid}/price/${vars.pricid}`, 'DELETE'),
      'Failed to delete the price'
    ),
    onSuccess: invalidate,
  });
};

/** Boost/promote a listing (admin, no payment). `POST /:eid/promotions`. */
export const useCreateEntityPromotionMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; promotion?: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.eid}/promotions`, 'POST', vars.promotion ?? {}),
      'Failed to boost the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Reassign a listing's owner. `POST /:eid/reassign` — `{ uid?, oid? }`. */
export const useReassignEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; uid?: number; oid?: number }) =>
        request<any>(`${BASE}/${vars.eid}/reassign`, 'POST', {
          ...(vars.uid != null ? { uid: vars.uid } : {}),
          ...(vars.oid != null ? { oid: vars.oid } : {}),
        }),
      'Failed to reassign the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Merge duplicates into a survivor. `POST /:eid/merge`. */
export const useMergeEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: {
        eid: number;
        merge_eids: number[];
        move_assets?: boolean;
        move_reviews?: boolean;
      }) =>
        request<any>(`${BASE}/${vars.eid}/merge`, 'POST', {
          merge_eids: vars.merge_eids,
          move_assets: vars.move_assets ?? true,
          move_reviews: vars.move_reviews ?? true,
        }),
      'Failed to merge listings'
    ),
    onSuccess: invalidate,
  });
};

/** Reorder media / set cover. `PUT /:eid/assets/reorder`. */
export const useReorderEntityAssetsMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: {
        eid: number;
        order?: Array<{ asid: number; order_by: number }>;
        primary_asid?: number;
      }) =>
        request<any>(`${BASE}/${vars.eid}/assets/reorder`, 'PUT', {
          ...(vars.order ? { order: vars.order } : {}),
          ...(vars.primary_asid != null ? { primary_asid: vars.primary_asid } : {}),
        }),
      'Failed to reorder assets'
    ),
    onSuccess: invalidate,
  });
};

/**
 * Archive (soft-delete) a single media asset. `DELETE /:eid/assets/:asid`.
 * Reversible via the restore endpoint — the UI surfaces this as "Archive".
 * No JSON body, so no proxy allowlist entry is required.
 */
export const useDeleteEntityAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; asid: number }) =>
        request<any>(`${BASE}/${vars.eid}/assets/${vars.asid}`, 'DELETE'),
      'Failed to archive the photo'
    ),
    onSuccess: invalidate,
  });
};

/** Restore a soft-deleted media asset. `POST /:eid/assets/:asid/restore`. */
export const useRestoreEntityAssetMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; asid: number }) =>
        request<any>(`${BASE}/${vars.eid}/assets/${vars.asid}/restore`, 'POST'),
      'Failed to restore the photo'
    ),
    onSuccess: invalidate,
  });
};

// ─── Bulk operations ─────────────────────────────────────────────────────────

/** Set one flag on many listings. `POST /bulk/flag`. */
export const useBulkFlagEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eids: number[]; field: EntityFlagField; value: 0 | 1 }) =>
        request<any>(`${BASE}/bulk/flag`, 'POST', vars),
      'Failed to bulk-update flags'
    ),
    onSuccess: invalidate,
  });
};

/** Transition many listings. `POST /bulk/transition`. */
export const useBulkTransitionEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eids: number[]; status: EntityStatus }) =>
        request<any>(`${BASE}/bulk/transition`, 'POST', vars),
      'Failed to bulk-transition listings'
    ),
    onSuccess: invalidate,
  });
};

/** Recategorize many listings. `POST /bulk/recategorize` — `{ eids, etid }`. */
export const useBulkRecategorizeEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eids: number[]; etid: number }) =>
        request<any>(`${BASE}/bulk/recategorize`, 'POST', vars),
      'Failed to recategorize listings'
    ),
    onSuccess: invalidate,
  });
};

/** Create a listing. `POST /` (base). */
export const useCreateEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (fields: Record<string, unknown>) => request<any>(BASE, 'POST', fields),
      'Failed to create the listing'
    ),
    onSuccess: invalidate,
  });
};

/** Bulk-create from JSON rows. `POST /import` — `{ rows: [...] }`. */
export const useImportEntitiesMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { rows: Array<Record<string, unknown>> }) =>
        request<any>(`${BASE}/import`, 'POST', vars),
      'Failed to import listings'
    ),
    onSuccess: invalidate,
  });
};

/** Reconcile search-index flag drift. `POST /reconcile-search`. */
export const useReconcileSearchMutation = () => {
  const request = useClientStore((s) => s.request);
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { apply?: boolean; limit?: number } = {}) =>
        request<any>(`${BASE}/reconcile-search`, 'POST', {
          apply: vars.apply ?? true,
          ...(vars.limit != null ? { limit: vars.limit } : {}),
        }),
      'Failed to reconcile search flags'
    ),
  });
};
/**
 * Suggest a change to the owner. `POST /:eid/suggest-change` (emails the listing
 * owner the suggestion). Backend: admin_entity_route, url_prefix
 * `/api/admin/entities` → full path `/api/admin/entities/:eid/suggest-change`.
 * Body `{ suggested_change }` — must stay allowlisted in the proxy or it 400s
 * with `missing_suggested_change`.
 */
export const useSuggestEditMutation = () => {
  const request = useClientStore((s) => s.request);
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; suggested_change: string }) =>
        request<any>(`${BASE}/${vars.eid}/suggest-change`, 'POST', {
          suggested_change: vars.suggested_change,
        }),
      'Failed to suggest a change'
    ),
  });
};

/**
 * Duplicate a listing on the seller's behalf. `POST /:eid/duplicate`
 * (ff-api#1302). Same service function as the seller's own door: spec, photos
 * and prices carry over, identity/verification/promotion/reach do not, and the
 * copy is a draft. `reason` is required and lands in the admin audit.
 */
export const useDuplicateEntityMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useEntityInvalidator();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { eid: number; reason: string; name?: string }) =>
        request<{ eid: number; uuid?: string; assets_copied?: number; prices_copied?: number }>(
          `${BASE}/${vars.eid}/duplicate`,
          'POST',
          { reason: vars.reason, ...(vars.name ? { name: vars.name } : {}) },
        ),
      'Failed to duplicate the listing'
    ),
    onSuccess: invalidate,
  });
};
